<template>
  <JdsSidePanel @close="$emit('close')">
    <template #header>
      <div>{{ headerText }}</div>
    </template>

    <template #body>
      <div class="fe-mb-m fe-mt-s">
        <div v-if="!questionsList?.length">
          <EmptyQnA
            class="fe-mb-l"
            text="No questions available for the product."
          />
        </div>

        <div v-else>
          <InfiniteScroll
            :is-loading-data="qnaPageMeta.loading"
            :is-infinite-scroll="true"
            :current-page="qnaPageMeta.current"
            :has-next="qnaPageMeta.hasNext"
            className="fe-py-xs"
            @load-more="fetchMoreQnA"
          >
            <AllQnAList
              v-for="question in questionsList"
              :qna="question"
              :key="question.id"
            />
          </InfiniteScroll>
        </div>
      </div>
    </template>

    <template #footer>
      <div>
        <div
          class="fe-flex fe-justify-between fe-items-center fe-text-body-xxs fe-text-primary-grey-60 fe-mb-xs"
        >
          <div>Your question will be reviewed and posted in 1-2 days.</div>
        </div>
        <div class="fe-flex fe-items-end fe-justify-between fe-gap-s">
          <div class="fe-shrink-0 fe-grow">
            <JdsTextArea
              placeholder="Type your question here.."
              :required="true"
              :min-length="questionConfig?.content?.minCharLimit"
              :max-length="questionConfig?.content?.maxCharLimit"
              :value="questionText"
              @input="handleQuestionText"
              :state="questionTextError ? 'error' : 'none'"
              :state-text="questionTextError"
              textAreaClassname="fe-rounded-medium"
            />
          </div>
          <div class="fe-shrink-0">
            <JdsButton
              variant="primary"
              size="small"
              :iconOnly="true"
              :disabled="!!questionTextError"
              @click="handleSubmit"
            >
              <template #icon>
                <JdsIcon
                  name="SendMessageIcon"
                  size="m"
                  color="fe-text-white"
                />
              </template>
            </JdsButton>
          </div>
        </div>
      </div>
    </template>
  </JdsSidePanel>
</template>

<script>
  import EmptyQnA from "@/components/QuestionAndAnswer/EmptyQnA/EmptyQnA.vue"
  import AllQnAList from "@/components/QuestionAndAnswer/AllQnAList/AllQnAList.vue"
  import InfiniteScroll from "@/components/Utilities/InfiniteScroll/InfiniteScroll.vue"
  import { modalMixin } from "@/mixins/modalMixin"
  import { toastMixin } from "@/mixins/toastMixin"
  import { apiAddQuestion } from "@/api_service/qnaApi"
  import JdsSidePanel from "@/components/DesignSystem/JdsSidePanel/JdsSidePanel.vue"
  import JdsTextArea from "@/components/DesignSystem/JdsTextArea/JdsTextArea.two.vue"
  import JdsButton from "@/components/DesignSystem/JdsButton/JdsButton.vue"
  import JdsIcon from "@/components/DesignSystem/JdsIcon/JdsIcon.vue"

  export default {
    name: "QnAListModal",
    components: {
      EmptyQnA,
      AllQnAList,
      InfiniteScroll,
      JdsSidePanel,
      JdsTextArea,
      JdsButton,
      JdsIcon,
    },
    mixins: [modalMixin, toastMixin],
    inject: ["globalState", "getProductQnA"],
    data() {
      return {
        questionText: "",
        questionTextError: "",
        loading: false,
      }
    },
    watch: {
      questionText() {
        if (this.questionTextError) {
          if (this.validateQuestionText()) {
            this.questionTextError = ""
          }
        }
      },
    },
    computed: {
      headerText() {
        const isAnyQuestionAnswered = this.questionsList?.find(
          (questionItem) => questionItem?.question?.numberOfAnswers > 0,
        )

        if (isAnyQuestionAnswered) {
          return "Questions and Answers"
        }

        return "Questions & Answers"
      },
      questionsList() {
        return this.globalState.qnA
      },
      qnaPageMeta() {
        return this.globalState.qnaPageMeta || {}
      },
      questionConfig() {
        return this.globalState.widgetConfig?.question_properties
      },
      questionHelperText() {
        const maxCharLength = this.questionConfig?.content?.maxCharLimit
        return `${this.questionText.length}/${maxCharLength}`
      },
      productData() {
        return this.globalState.product
      },
    },
    methods: {
      fetchMoreQnA(pageNoToBeFetched = 0) {
        this.getProductQnA(pageNoToBeFetched)
      },
      handleQuestionText(questionText) {
        this.questionText = questionText
      },
      validateQuestionText() {
        const config = this.questionConfig?.content

        const charLength = this.questionText.length

        const { minCharLimit = 0, maxCharLimit = 0 } = config

        // If content is hidden, then no need to check for validity
        // If content string is empty and it is not required (as per config), then no need to check for validity
        if (config.hidden || (!config.required && !charLength)) {
          return true
        }

        // If content is filled by user, then it doesn't matter, if it is required or not, it should met min and max character guideline
        if (
          charLength &&
          charLength >= minCharLimit &&
          charLength <= maxCharLimit
        ) {
          return true
        }

        const errorMessages = {
          contentError: "Please provide your question",
          minError: `Minimum ${minCharLimit} characters required`,
          maxError: `Maximum ${minCharLimit} characters required`,
        }

        if (!charLength) {
          this.questionTextError = errorMessages.contentError
        } else if (charLength < minCharLimit) {
          this.questionTextError = errorMessages.minError
        } else if (charLength > maxCharLimit) {
          this.questionTextError = errorMessages.maxError
        }

        return false
      },
      async handleSubmit() {
        try {
          this.loading = true

          if (!this.validateQuestionText()) {
            this.loading = false
            return
          }

          const requestData = {
            productId: this.productData.productId,
            productURL: this.productData.productURL,
            alternateCode1: this.productData.alternateCode1,
            alternateCode2: this.productData.alternateCode2,
            categoryL1: this.productData.categoryL1,
            categoryL2: this.productData.categoryL2,
            categoryL3: this.productData.categoryL3,
            categoryL4: this.productData.categoryL4,
            question: this.questionText.trim(),
          }

          const response = await apiAddQuestion(requestData)

          this.loading = false

          this.$emit("close")
          this.showToast("Question Submitted", "success")

          return response?.data
        } catch (error) {
          this.loading = false
          this.showToast(error?.message, "error")
        }
      },
    },
  }
</script>
